import BasePlatformFeatureList from 'components/download-page/BasePlatformFeatureList'
import BaseHeader from 'components/download-page/BasePlatformHeader'
import DocumentationLinks from 'components/download-page/DocumentationLinks'
import PlatformDownloadSection from 'components/download-page/PlatformDownloadSection'
import PlatformSelector from 'components/download-page/PlatformSelector'
import PlatformTitle from 'components/download-page/PlatformTitle'
import Tag from 'components/Tag'
import UnstyledMarkdown from 'components/UI/UnstyledMarkdown'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import { horizontalImageStyle } from 'pages/download'
import { Dispatch, SetStateAction } from 'react'
import { Flex, Text } from 'theme-ui'
import { AvailablePlatforms } from 'utils/downloadPageUtils'
import getEnglishStrings from 'utils/getEnglishStrings'
import useCurrentPlatform from 'utils/useCurrentPlatform'

export default function ExtensionPlatform({
  platformName,
  setPlatform,
  extensionRef,
}: {
  platformName: AvailablePlatforms
  setPlatform: Dispatch<SetStateAction<AvailablePlatforms>>
  extensionRef: React.RefObject<HTMLDivElement>
}): JSX.Element {
  const translations = useStaticQuery<Queries.ExtensionPlatformQuery>(graphql`
    query ExtensionPlatform {
      directus {
        download_page {
          strings {
            languages_code {
              code
            }
            windscribe_for_chrome
            windscribe_for_firefox
            windscribe_for_edge
            standalone_proxy
            over_a_dozen_built_in
            does_not_require
            desktop_app_alone
            the_windscribe_desktop_app
          }
        }
        general_translations {
          strings {
            languages_code {
              code
            }
            browser
          }
        }
      }
    }
  `)

  const downloadPageStrings = getEnglishStrings(translations.directus.download_page?.strings)
  const generalTranslationStrings = getEnglishStrings(
    translations.directus.general_translations?.strings,
  )

  const { platform } = useCurrentPlatform()
  const isDesktop = platform === 'desktop'

  return (
    <BaseHeader>
      <Tag title={generalTranslationStrings?.browser ?? ''} />
      {isDesktop && (
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Text
            sx={{
              fontSize: ['2.4rem', '3.2rem', '3.2rem'],
              fontWeight: 'bold',
              maxWidth: ['54rem', '77rem', '70%'],
              textAlign: 'center',
            }}
          >
            {downloadPageStrings?.desktop_app_alone}
          </Text>
          <Text
            sx={{
              fontSize: '2.1rem',
              color: 'white50',
              maxWidth: ['40rem', '60rem', '50%'],
              textAlign: 'center',
              mt: ['1.6rem', '2.8rem', '3.6rem'],
            }}
          >
            <UnstyledMarkdown>
              {downloadPageStrings?.the_windscribe_desktop_app ?? ''}
            </UnstyledMarkdown>
          </Text>
        </Flex>
      )}

      <PlatformSelector
        platformName={platformName}
        setPlatform={setPlatform}
        availablePlatforms={['Chrome', 'Firefox', 'Edge']}
      />
      <Flex
        sx={{
          justifyContent: 'space-between',
          flexDirection: ['column-reverse', 'row', 'row'],
          alignItems: ['center', 'flex-start'],
          width: '100%',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: ['3.2rem', '4.8rem'],
            flexGrow: 1,
            minWidth: ['auto', '38.5rem'],
          }}
        >
          <PlatformTitle
            title={
              {
                Chrome: downloadPageStrings?.windscribe_for_chrome ?? '',
                Edge: downloadPageStrings?.windscribe_for_edge ?? '',
                Firefox: downloadPageStrings?.windscribe_for_firefox ?? '',
              }[platformName]
            }
          />
          <PlatformDownloadSection platformName={platformName} />
          <Flex sx={{ flexDirection: 'column', gap: '2.4rem' }}>
            <BasePlatformFeatureList
              featureList={[
                downloadPageStrings?.standalone_proxy ?? '',
                downloadPageStrings?.over_a_dozen_built_in ?? '',
                downloadPageStrings?.does_not_require ?? '',
              ]}
            />
            <DocumentationLinks platformName={platformName} />
          </Flex>
        </Flex>
        <Flex
          sx={{ maxWidth: '45.8rem', flexShrink: 1 }}
          ref={extensionRef}
          data-testid="extension-platform"
        >
          {
            <>
              <StaticImage
                src="../../images/windscribe-extension-chrome.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...horizontalImageStyle,
                  display: platformName === 'Chrome' ? 'flex' : 'none',
                }}
              />
              <StaticImage
                src="../../images/windscribe-extension-firefox.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...horizontalImageStyle,
                  display: platformName === 'Firefox' ? 'flex' : 'none',
                }}
                imgStyle={{
                  objectFit: 'scale-down',
                }}
              />
              <StaticImage
                src="../../images/windscribe-extension-edge.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...horizontalImageStyle,
                  display: platformName === 'Edge' ? 'flex' : 'none',
                }}
                imgStyle={{
                  objectFit: 'scale-down',
                }}
              />
            </>
          }
        </Flex>
      </Flex>
    </BaseHeader>
  )
}
